import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { usePortalAuth, formatApiErrorDetail } from "@/components/portal/PortalAuth";
import { SEO } from "@/components/site/SEO";

export default function PortalLogin() {
  const { user, login } = usePortalAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) navigate(user.role === "installer" ? "/portal/jobs" : "/portal", { replace: true });
  }, [user, navigate]);

  if (user) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const data = await login(email, password);
      navigate(data.role === "installer" ? "/portal/jobs" : "/portal", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-warm-50 grain relative flex items-center justify-center px-4">
      <SEO title="Portal Login | My Blinds Company" description="Internal portal login." path="/portal/login" />
      <div className="w-full max-w-sm">
        <div className="rounded-2xl border border-warm-200 bg-white p-8 shadow-xl" data-testid="portal-login-card">
          <img src="/logo.png" alt="My Blinds Company logo" className="h-12 w-auto rounded mx-auto mb-6" />
          <h1 className="font-serif text-2xl text-slate-900 text-center">Team Portal</h1>
          <p className="text-sm text-slate-500 text-center mt-1 mb-7">Sign in to view and manage enquiries.</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label htmlFor="portal-email" className="text-sm font-medium text-slate-700 block mb-1.5">Email</label>
              <input
                id="portal-email"
                data-testid="portal-email-input"
                type="email"
                required
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-xl border border-warm-300 bg-white px-4 py-3 text-base focus:border-navy-900 focus:outline-none"
              />
            </div>
            <div>
              <label htmlFor="portal-password" className="text-sm font-medium text-slate-700 block mb-1.5">Password</label>
              <input
                id="portal-password"
                data-testid="portal-password-input"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-xl border border-warm-300 bg-white px-4 py-3 text-base focus:border-navy-900 focus:outline-none"
              />
            </div>
            {error && (
              <p role="alert" data-testid="portal-login-error" className="text-sm text-red-700">{error}</p>
            )}
            <button
              type="submit"
              data-testid="portal-login-submit"
              disabled={busy}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-navy-900 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-navy-800 disabled:opacity-50"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
              Sign In
            </button>
          </form>
        </div>
        <p className="text-center text-xs text-slate-400 mt-6">
          <a href="/" className="underline hover:text-slate-600">Back to website</a>
        </p>
      </div>
    </div>
  );
}
